import { useScienceTutorSection, tutorSelection } from './scienceTutorContext';
import { ArrowDown, ArrowLeft, ArrowUp, CheckCircle2, Magnet, RotateCcw, XCircle } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';

import { BoilingPointChart } from './IntermolecularBoilingChart';
import {
  IMF_CONDITIONS,
  IMF_DATA_SOURCES,
  IMF_LABELS,
  IMF_TYPE_ORDER,
  INTERMOLECULAR_FORCES_START_YEAR,
  INTERMOLECULAR_FORCES_TOOL_IDENTITY,
  RANKING_CHALLENGES,
  SUBSTANCES,
  boilingPointRows,
  checkIdentification,
  checkRanking,
  formatCelsius,
  substanceById,
  type IdentifyResult,
  type ImfType,
  type RankingResult,
} from './intermolecularForces';
import { StudyShell } from './StudyShell';
import { useChemistryToolYear } from './useChemistryToolYear';

const IMF_DEFINITIONS: Record<ImfType, string> = {
  dispersion: 'Temporary dipoles from moving electrons. Present in every substance; stronger with more electrons.',
  'dipole-dipole': 'Attraction between permanent dipoles of polar molecules.',
  'hydrogen-bonding': 'H bonded to N, O or F is attracted to a lone pair on N, O or F of another molecule.',
};

function Feedback({ correct, label, children }: { correct: boolean; label: string; children: ReactNode }) {
  return (
    <div
      role="region"
      aria-label={label}
      className={`mt-4 rounded-2xl border p-4 ${
        correct ? 'border-brand-mint/45 bg-wash-mint' : 'border-brand-coral/45 bg-wash-coral'
      }`}
    >
      <p className="flex items-center gap-2 text-sm font-extrabold text-ink">
        {correct ? (
          <CheckCircle2 className="h-4 w-4 text-brand-mint" aria-hidden="true" />
        ) : (
          <XCircle className="h-4 w-4 text-brand-coral" aria-hidden="true" />
        )}
        {correct ? 'Correct' : 'Not yet'}
      </p>
      <div className="mt-1 space-y-1 text-sm font-medium leading-6 text-ink-soft">{children}</div>
    </div>
  );
}

function IdentifyActivity() {
  const [substanceId, setSubstanceId] = useState(SUBSTANCES[0].id);
  const [selected, setSelected] = useState<ImfType[]>([]);
  useScienceTutorSection('IdentifyActivity', { substanceId, selected: tutorSelection(selected) });
  const [result, setResult] = useState<IdentifyResult | null>(null);
  const substance = substanceById(substanceId) ?? SUBSTANCES[0];

  const choose = (id: string) => {
    setSubstanceId(id);
    setSelected([]);
    setResult(null);
  };
  const toggle = (type: ImfType) => {
    setSelected((current) => (current.includes(type) ? current.filter((entry) => entry !== type) : [...current, type]));
    setResult(null);
  };

  return (
    <section
      aria-labelledby="identify-heading"
      className="min-w-0 rounded-[2rem] border border-hairline bg-canvas-pure p-5 shadow-card-soft sm:p-6 lg:p-8"
    >
      <p className="text-xs font-extrabold uppercase tracking-[0.14em] text-brand-coral">Activity 1</p>
      <h2 id="identify-heading" className="mt-2 text-2xl font-extrabold text-ink">
        Which forces act between the molecules?
      </h2>
      <div className="mt-4 flex flex-wrap gap-2" role="group" aria-label="Choose a substance">
        {SUBSTANCES.map((candidate) => (
          <button
            key={candidate.id}
            type="button"
            aria-pressed={candidate.id === substance.id}
            aria-label={candidate.name}
            onClick={() => choose(candidate.id)}
            className={`rounded-full border px-3 py-1.5 text-xs font-extrabold ${
              candidate.id === substance.id ? 'border-brand-sky bg-wash-sky text-ink' : 'border-hairline bg-white text-slate2'
            }`}
          >
            {candidate.formula}
          </button>
        ))}
      </div>

      <div className="mt-6 grid gap-6 lg:grid-cols-[minmax(0,0.8fr)_minmax(0,1fr)]">
        <div className="rounded-[1.5rem] border border-brand-sky/25 bg-gradient-to-br from-white to-wash-sky p-5">
          <p className="text-4xl font-extrabold text-ink">{substance.formula}</p>
          <p className="mt-1 text-sm font-extrabold text-slate2">{substance.name}</p>
          <dl className="mt-4 grid gap-2 text-sm font-medium text-ink-soft">
            <div className="flex justify-between gap-3">
              <dt>Molar mass</dt>
              <dd className="font-extrabold text-ink">{substance.molarMass} g/mol</dd>
            </div>
          </dl>
          <p className="mt-4 text-xs font-medium leading-5 text-slate2">
            Decide whether the molecule is polar and whether it has H bonded directly to N, O or F.
          </p>
        </div>

        <div className="min-w-0">
          <fieldset>
            <legend className="text-sm font-extrabold text-ink">Select every force present</legend>
            <div className="mt-3 grid gap-2">
              {IMF_TYPE_ORDER.map((type) => (
                <label
                  key={type}
                  className={`flex cursor-pointer items-start gap-3 rounded-2xl border px-4 py-3 ${
                    selected.includes(type) ? 'border-brand-coral bg-wash-coral' : 'border-hairline bg-white'
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={selected.includes(type)}
                    onChange={() => toggle(type)}
                    className="mt-1 accent-brand-coral"
                  />
                  <span>
                    <span className="block text-sm font-extrabold text-ink">{IMF_LABELS[type]}</span>
                    <span className="block text-xs font-medium leading-5 text-slate2">{IMF_DEFINITIONS[type]}</span>
                  </span>
                </label>
              ))}
            </div>
          </fieldset>
          <button
            type="button"
            disabled={selected.length === 0}
            onClick={() => setResult(checkIdentification(substance, selected))}
            className="mt-4 rounded-full bg-ink px-5 py-3 text-sm font-extrabold text-white disabled:cursor-not-allowed disabled:opacity-40"
          >
            Check forces
          </button>
          {result ? (
            <Feedback correct={result.correct} label="Forces feedback">
              {result.feedback.map((line) => (
                <p key={line}>{line}</p>
              ))}
              {result.correct ? (
                <p>
                  Normal boiling point:{' '}
                  <strong>
                    {substance.boilingPointC === null ? 'none (sublimes)' : formatCelsius(substance.boilingPointC)}
                  </strong>
                </p>
              ) : null}
            </Feedback>
          ) : null}
        </div>
      </div>
    </section>
  );
}

function RankingActivity() {
  const [challengeId, setChallengeId] = useState(RANKING_CHALLENGES[0].id);
  const challenge = RANKING_CHALLENGES.find((candidate) => candidate.id === challengeId) ?? RANKING_CHALLENGES[0];
  const [order, setOrder] = useState<string[]>(challenge.substanceIds);
  useScienceTutorSection('RankingActivity', { challengeId, order });
  const [result, setResult] = useState<RankingResult | null>(null);
  const [completed, setCompleted] = useState<string[]>([]);

  const choose = (id: string) => {
    const next = RANKING_CHALLENGES.find((candidate) => candidate.id === id) ?? RANKING_CHALLENGES[0];
    setChallengeId(next.id);
    setOrder(next.substanceIds);
    setResult(null);
  };
  const move = (index: number, offset: number) => {
    const target = index + offset;
    if (target < 0 || target >= order.length) return;
    const next = [...order];
    [next[index], next[target]] = [next[target], next[index]];
    setOrder(next);
    setResult(null);
  };
  const check = () => {
    const outcome = checkRanking(challenge, order);
    setResult(outcome);
    if (outcome?.correct) setCompleted((current) => (current.includes(challenge.id) ? current : [...current, challenge.id]));
  };

  return (
    <section
      aria-labelledby="ranking-heading"
      className="min-w-0 rounded-[2rem] border border-hairline bg-canvas-pure p-5 shadow-card-soft sm:p-6 lg:p-8"
    >
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <p className="text-xs font-extrabold uppercase tracking-[0.14em] text-brand-sky">Activity 2</p>
          <h2 id="ranking-heading" className="mt-2 text-2xl font-extrabold text-ink">
            Rank by boiling point, then reveal the data
          </h2>
        </div>
        <span className="shrink-0 rounded-full bg-wash-mint px-3 py-1 text-xs font-extrabold text-ink-soft">
          {completed.length} of {RANKING_CHALLENGES.length} ranked correctly
        </span>
      </div>
      <div className="mt-4 flex flex-wrap gap-2" role="group" aria-label="Choose a ranking challenge">
        {RANKING_CHALLENGES.map((candidate) => (
          <button
            key={candidate.id}
            type="button"
            aria-pressed={candidate.id === challenge.id}
            onClick={() => choose(candidate.id)}
            className={`rounded-full border px-3 py-1.5 text-xs font-extrabold ${
              candidate.id === challenge.id ? 'border-brand-sky bg-wash-sky text-ink' : 'border-hairline bg-white text-slate2'
            }`}
          >
            {completed.includes(candidate.id) ? '✓ ' : ''}
            {candidate.title}
          </button>
        ))}
      </div>

      <div className="mt-6 grid gap-6 lg:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)]">
        <div className="min-w-0">
          <p className="text-sm font-extrabold text-ink">{challenge.focus}</p>
          <p className="mt-1 text-xs font-medium text-slate2">Order from lowest (top) to highest (bottom) boiling point.</p>
          <ol className="mt-3 grid gap-2" aria-label="Your boiling point order">
            {order.map((id, index) => {
              const substance = substanceById(id);
              if (!substance) return null;
              return (
                <li key={id} className="flex items-center gap-2 rounded-2xl border border-hairline bg-white px-3 py-2">
                  <span className="w-6 text-center text-xs font-extrabold text-slate2">{index + 1}</span>
                  <span className="min-w-0 flex-1">
                    <span className="block text-sm font-extrabold text-ink">{substance.formula}</span>
                    <span className="block text-xs font-medium text-slate2">
                      {substance.name} · {substance.molarMass} g/mol
                    </span>
                  </span>
                  <button
                    type="button"
                    aria-label={`Move ${substance.name} up`}
                    disabled={index === 0}
                    onClick={() => move(index, -1)}
                    className="rounded-full border border-hairline p-2 text-ink-soft disabled:opacity-30"
                  >
                    <ArrowUp className="h-4 w-4" aria-hidden="true" />
                  </button>
                  <button
                    type="button"
                    aria-label={`Move ${substance.name} down`}
                    disabled={index === order.length - 1}
                    onClick={() => move(index, 1)}
                    className="rounded-full border border-hairline p-2 text-ink-soft disabled:opacity-30"
                  >
                    <ArrowDown className="h-4 w-4" aria-hidden="true" />
                  </button>
                </li>
              );
            })}
          </ol>
          <div className="mt-4 flex flex-wrap gap-3">
            <button
              type="button"
              onClick={check}
              className="rounded-full bg-ink px-5 py-3 text-sm font-extrabold text-white"
            >
              Reveal boiling points
            </button>
            <button
              type="button"
              onClick={() => choose(challenge.id)}
              className="inline-flex items-center gap-2 rounded-full border border-hairline bg-white px-5 py-3 text-sm font-extrabold text-ink-soft"
            >
              <RotateCcw className="h-4 w-4" aria-hidden="true" /> Reset
            </button>
          </div>
        </div>

        <div className="min-w-0">
          {result ? (
            <>
              <Feedback correct={result.correct} label="Ranking feedback">
                <p>{result.feedback}</p>
                {!result.correct ? <p>{challenge.explanation}</p> : null}
              </Feedback>
              <div className="mt-4 rounded-[1.5rem] border border-brand-sky/25 bg-white p-4">
                <BoilingPointChart rows={boilingPointRows(result.correctOrder)} label="Boiling points for this challenge" />
              </div>
            </>
          ) : (
            <p className="rounded-2xl bg-surface px-4 py-3 text-sm font-medium leading-6 text-slate2">
              Predict first. The real boiling points and a bar chart appear after you reveal.
            </p>
          )}
        </div>
      </div>
    </section>
  );
}

export function IntermolecularForcesLabPage() {
  const year = useChemistryToolYear();
  const [showAll, setShowAll] = useState(false);
  const co2 = substanceById('co2');

  return (
    <StudyShell scienceTutor={{ subjectKey: 'chemistry', toolId: 'intermolecular-forces', state: { showAll } }}>
      <Link
        to="/learn/study/subjects/chemistry/tools"
        className="inline-flex items-center gap-2 text-sm font-extrabold text-slate2 hover:text-ink"
      >
        <ArrowLeft className="h-4 w-4" aria-hidden="true" /> Chemistry tools
      </Link>

      <header
        className="chemistry-tool-header mt-5 rounded-[2rem] border border-brand-sky/25 bg-gradient-to-br from-wash-sky via-canvas-pure to-wash-mint p-6 shadow-card-soft lg:p-8"
        data-tool-key={INTERMOLECULAR_FORCES_TOOL_IDENTITY.toolKey}
        data-tool-version={INTERMOLECULAR_FORCES_TOOL_IDENTITY.toolVersion}
      >
        <div className="flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
          <div className="max-w-3xl">
            <p className="flex items-center gap-2 text-xs font-extrabold uppercase tracking-[0.16em] text-brand-sky">
              <Magnet className="h-4 w-4" aria-hidden="true" /> Structure and bonding
            </p>
            <h1 className="mt-3 text-3xl font-extrabold tracking-tight text-ink sm:text-4xl">Intermolecular Forces Lab</h1>
            <p className="mt-3 max-w-2xl font-medium leading-7 text-slate2">
              Identify dispersion, dipole–dipole and hydrogen bonding forces, then use them to predict boiling points.
            </p>
          </div>
          <div className="rounded-2xl border border-white/80 bg-white/80 px-4 py-3 text-sm font-bold text-ink-soft">
            Year {INTERMOLECULAR_FORCES_START_YEAR}–12 · explore only
          </div>
        </div>
      </header>

      {year !== null && year < INTERMOLECULAR_FORCES_START_YEAR ? (
        <p role="note" className="mt-4 rounded-2xl border border-brand-sunshine/50 bg-wash-sunshine px-5 py-3 text-sm font-medium text-ink-soft">
          This tool starts at Year {INTERMOLECULAR_FORCES_START_YEAR}. You are viewing Year {year}; you can still explore it.
        </p>
      ) : null}

      <div className="my-6 space-y-6">
        <IdentifyActivity />
        <RankingActivity />

        <section
          aria-labelledby="all-data-heading"
          className="min-w-0 rounded-[2rem] border border-hairline bg-canvas-pure p-5 shadow-card-soft sm:p-6 lg:p-8"
        >
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <h2 id="all-data-heading" className="text-xl font-extrabold text-ink">
              All boiling-point data
            </h2>
            <button
              type="button"
              aria-expanded={showAll}
              aria-controls="all-data-chart"
              onClick={() => setShowAll((open) => !open)}
              className="rounded-full border border-hairline bg-white px-4 py-2 text-sm font-extrabold text-ink-soft"
            >
              {showAll ? 'Hide all data' : 'Show all data'}
            </button>
          </div>
          {showAll ? (
            <div id="all-data-chart" className="mt-4">
              <BoilingPointChart rows={boilingPointRows()} label="Normal boiling points of all reviewed substances" />
              {co2 ? <p className="mt-3 text-xs font-medium leading-5 text-slate2">Not charted: {co2.formula}. {co2.note}</p> : null}
            </div>
          ) : null}
        </section>
      </div>

      <footer className="my-6 rounded-[1.5rem] border border-hairline bg-surface px-5 py-4 text-xs font-medium leading-5 text-slate2">
        {IMF_CONDITIONS} Data: {IMF_DATA_SOURCES.join(' · ')}. Pending teaching review. “Strongest force”
        names the strongest type present; for similar polar molecules such as HCl and HBr, the difference
        in dispersion forces can decide the order. This tool does not save an attempt, record mastery or
        claim alignment to a state syllabus.
      </footer>
    </StudyShell>
  );
}
